import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MessageCircle, X, Send, Loader2, Sparkles } from 'lucide-react';
import { track } from '../lib/analytics';

const STORAGE_KEY = 'gh_chat_session';
const HISTORY_KEY = 'gh_chat_history';

const genId = () => `s-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
const msgId = () => `m-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

// Dev-only logger — avoids leaking internal warnings in production bundles.
const devWarn = (...args) => {
  if (process.env.NODE_ENV === 'development') console.warn(...args);
};

const seedMessage = {
  id: 'seed',
  role: 'assistant',
  content:
    "Hey. I'm the AI concierge for Growth Hackeng. Ask me anything about email marketing, pricing, or what Akash could ship for your list. If we hit something too specific, I'll point you to the free audit.",
};

const AskAkashWidget = () => {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const [messages, setMessages] = useState([seedMessage]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const scrollerRef = useRef(null);
  const inputRef = useRef(null);
  const abortRef = useRef(null);

  // Session + history hydration
  useEffect(() => {
    let s = localStorage.getItem(STORAGE_KEY);
    if (!s) {
      s = genId();
      localStorage.setItem(STORAGE_KEY, s);
    }
    setSessionId(s);
    try {
      const h = JSON.parse(localStorage.getItem(HISTORY_KEY) || '[]');
      if (Array.isArray(h) && h.length) setMessages(h);
    } catch (err) {
      devWarn('AskAkash: could not restore chat history', err);
    }
  }, []);

  // Persist history
  useEffect(() => {
    try { localStorage.setItem(HISTORY_KEY, JSON.stringify(messages)); }
    catch (err) { devWarn('AskAkash: could not persist chat history', err); }
  }, [messages]);

  // Auto-scroll to bottom on new content
  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [messages, streaming, open]);

  // Focus input on open
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 60);
  }, [open]);

  const send = useCallback(async () => {
    const text = input.trim();
    if (!text || streaming || !sessionId) return;

    const userMsg = { id: msgId(), role: 'user', content: text };
    const assistantMsg = { id: msgId(), role: 'assistant', content: '' };
    setMessages((m) => [...m, userMsg, assistantMsg]);
    setInput('');
    setStreaming(true);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const apiBase = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${apiBase}/api/chat/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId, message: text }),
        signal: controller.signal,
      });
      if (!res.ok || !res.body) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || 'Chat is unavailable right now.');
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      // SSE parse: split on double newlines; each event is "data: {...}"
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        const parts = buffer.split('\n\n');
        buffer = parts.pop() || '';
        for (const raw of parts) {
          const line = raw.trim();
          if (!line.startsWith('data:')) continue;
          const payload = line.slice(5).trim();
          if (payload === '[DONE]') { continue; }
          try {
            const obj = JSON.parse(payload);
            if (obj.text) {
              setMessages((m) => {
                const copy = m.slice();
                const last = copy[copy.length - 1];
                copy[copy.length - 1] = { ...last, content: (last.content || '') + obj.text };
                return copy;
              });
            } else if (obj.error) {
              setMessages((m) => {
                const copy = m.slice();
                copy[copy.length - 1] = { ...copy[copy.length - 1], content: obj.error };
                return copy;
              });
            }
          } catch (err) {
            devWarn('AskAkash: skipping malformed SSE chunk', err);
          }
        }
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        setMessages((m) => {
          const copy = m.slice();
          copy[copy.length - 1] = {
            ...copy[copy.length - 1],
            content: "Something broke on my end. Try again, or hop over to the contact form and Akash will reply directly.",
          };
          return copy;
        });
      }
    } finally {
      setStreaming(false);
      abortRef.current = null;
    }
  }, [input, streaming, sessionId]);

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const resetChat = () => {
    if (streaming) abortRef.current?.abort();
    const s = genId();
    localStorage.setItem(STORAGE_KEY, s);
    setSessionId(s);
    setMessages([seedMessage]);
  };

  return (
    <>
      {/* Floating launcher — pill design with visible label */}
      <button
        type="button"
        onClick={() => {
          setOpen((v) => {
            if (!v) track('chat_widget_open');
            return !v;
          });
        }}
        data-testid="ask-akash-launcher"
        aria-label={open ? 'Close chat' : 'Open Ask Akash chat'}
        className={`fixed bottom-5 right-5 z-[90] flex items-center gap-2 rounded-full shadow-[0_20px_40px_-15px_rgba(234,88,12,0.55)] transition-all font-semibold ${
          open
            ? 'bg-ink text-[var(--gh-cream)] px-3 py-3 scale-95'
            : 'bg-[var(--gh-orange)] text-white hover:bg-[var(--gh-orange-deep)] hover:scale-[1.03] px-3 sm:px-5 py-3'
        }`}
      >
        {open ? (
          <X className="w-5 h-5" />
        ) : (
          <>
            <span className="relative flex items-center justify-center w-6 h-6 rounded-full bg-white/20">
              <MessageCircle className="w-3.5 h-3.5" />
              <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-white ring-2 ring-[var(--gh-orange)] animate-pulse" />
            </span>
            <span className="hidden sm:inline text-sm whitespace-nowrap">Ask Akash</span>
          </>
        )}
      </button>

      {/* Panel */}
      {open && (
        <div
          data-testid="ask-akash-panel"
          className="fixed z-[95] bottom-24 right-4 sm:right-5 w-[calc(100vw-2rem)] sm:w-[380px] h-[560px] max-h-[calc(100vh-8rem)] rounded-[1.5rem] bg-cream border border-line shadow-[0_30px_60px_-20px_rgba(10,10,10,0.35)] flex flex-col overflow-hidden animate-in"
        >
          {/* Header */}
          <div className="bg-[var(--gh-orange)] text-[var(--gh-cream)] px-4 py-3.5 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                <Sparkles className="w-4 h-4" />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-widest font-semibold opacity-85">Growth Hackeng</p>
                <p className="text-sm font-bold leading-tight">Ask Akash · AI concierge</p>
              </div>
            </div>
            <button
              onClick={resetChat}
              data-testid="ask-akash-reset"
              className="text-[11px] uppercase tracking-widest font-semibold px-2.5 py-1 rounded-full bg-white/15 hover:bg-white/25 transition-colors"
            >
              New chat
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollerRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-cream" data-testid="ask-akash-messages">
            {messages.map((m, i) => (
              <div key={m.id || i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-[14px] leading-relaxed whitespace-pre-wrap ${
                    m.role === 'user'
                      ? 'bg-ink text-[var(--gh-cream)] rounded-br-sm'
                      : 'bg-cream-2 text-ink border border-line rounded-bl-sm'
                  }`}
                  data-testid={`ask-akash-msg-${m.role}-${i}`}
                >
                  {m.content}
                  {m.role === 'assistant' && streaming && i === messages.length - 1 && (
                    <span className="inline-block ml-0.5 w-1.5 h-3 bg-[var(--gh-orange)] align-baseline animate-pulse" />
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Input */}
          <div className="border-t border-line bg-cream-2 px-3 py-3">
            <div className="flex items-end gap-2 bg-cream border border-line rounded-2xl px-3 py-2 focus-within:border-[var(--gh-orange)] focus-within:ring-2 focus-within:ring-[var(--gh-orange)]/20 transition-all">
              <textarea
                ref={inputRef}
                rows={1}
                value={input}
                onChange={(e) => {
                  setInput(e.target.value);
                  e.target.style.height = 'auto';
                  e.target.style.height = Math.min(120, e.target.scrollHeight) + 'px';
                }}
                onKeyDown={onKeyDown}
                placeholder="Ask about pricing, deliverability, or your list…"
                data-testid="ask-akash-input"
                className="flex-1 bg-transparent outline-none text-[14px] text-ink placeholder-muted-warm resize-none max-h-[120px]"
              />
              <button
                onClick={send}
                disabled={!input.trim() || streaming}
                data-testid="ask-akash-send"
                aria-label="Send"
                className="w-9 h-9 rounded-full bg-[var(--gh-orange)] hover:bg-[var(--gh-orange-deep)] text-white flex items-center justify-center transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
              >
                {streaming ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-[10px] text-muted-warm mt-2 text-center">
              AI concierge. Real answers from Akash come through the contact form.
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default AskAkashWidget;
